import React from 'react';
import { ProbingFormUpload } from '@/components/ProbingFormUpload';
import { ProbingFormPreview as UIProbingFormPreview } from '@shopee/chatbot-ui';
import { getTrans } from '@/utils/tools';
import { BUYER_ORDER_LIST_TITLE_TEXT, PROBING_FORM_EXP, PROBING_FORM_TRANSIFY, SELLER_ORDER_LIST_TITLE_TEXT, USER_TYPE_INDEX } from '@/utils/constants';
import { SubmitProbingFormParams } from '.';
import { formatOrder, oldTaskbotLoadMore } from '../PropsProtocol/OrderList';
import { getProbingFormExperiment } from '@/utils/getProbingFormExperiment';

export type ProbingFormPreviewProps = {
  probingForm: Omit<SubmitProbingFormParams, 'order_id'> & {
    desc: string;
    order: any;
  };
}

export default function ProbingFormPreview(props: ProbingFormPreviewProps) {
  const {
    user_type,
    enquiry_type,
    problem_type,
    order,
    desc: description,
    files,
  } = props.probingForm || {};
  const probingFormExperiment = getProbingFormExperiment();
  const STILL_NOT_FOUND_ORDER_ID = -1;
  const cannotFindOrder = {
    orderId: STILL_NOT_FOUND_ORDER_ID,
    orderSn: `${STILL_NOT_FOUND_ORDER_ID}`,
  };
  const formattedOrder = order?.order_sn ? formatOrder(order) : null;

  const getFormItemList = () => {
    const formItemList = [];

    if (probingFormExperiment === PROBING_FORM_EXP.Flow2) {
      formItemList.push({
        title: getTrans('enquiry_details_modal_category_title'),
        value: user_type.name,
      });

      formItemList.push({
        title: getTrans('enquiry_details_modal_option_title'),
        value: enquiry_type.name,
      });
    }

    if (problem_type?.id && problem_type?.name) {
      formItemList.push({
        title: getTrans(PROBING_FORM_TRANSIFY.PROBLEM_TYPE),
        value: problem_type.name,
      });
    }

    if (formattedOrder?.title) {
      formItemList.push({
        title: getTrans(PROBING_FORM_TRANSIFY.ORDER),
        value: formattedOrder?.title,
      });
    }

    if (order?.order_id === STILL_NOT_FOUND_ORDER_ID) {
      formItemList.push({
        title: getTrans(PROBING_FORM_TRANSIFY.ORDER),
        value: getTrans(PROBING_FORM_TRANSIFY.CANNOT_FIND_ORDER),
      });
    }

    if (description) {
      formItemList.push({
        title: getTrans(PROBING_FORM_TRANSIFY.DESCRIPTION),
        value: description,
        maxLines: 2,
      });
    }

    if (files && files.length) {
      formItemList.push({
        title: getTrans(PROBING_FORM_TRANSIFY.FILES),
        value: getTrans(PROBING_FORM_TRANSIFY.PREVIEW_FILE),
      });
    }

    return formItemList;
  };

  const getInitialValues = async () => {
    return {
      userType: {
        value: user_type?.id,
        content: user_type?.name,
      },
      enquiryType: {
        value: enquiry_type?.id,
        content: enquiry_type?.name,
      },
      problemType: {
        value: problem_type?.id as number,
        content: problem_type?.name as string,
      },
      order: order?.order_id === STILL_NOT_FOUND_ORDER_ID ? cannotFindOrder : formattedOrder as any,
      description,
      files
    };
  };

  return (
    <div>
      <UIProbingFormPreview
        title={getTrans(PROBING_FORM_TRANSIFY.PREVIEW_TITLE)}
        buttonText={getTrans(PROBING_FORM_TRANSIFY.PREVIEW_VIEW_MORE)}
        formList={getFormItemList()}
        popupProps={{ title: getTrans(PROBING_FORM_TRANSIFY.TITLE) }}
        probingFormProps={{
          userTabTypes: [
            { name: getTrans(BUYER_ORDER_LIST_TITLE_TEXT), type: USER_TYPE_INDEX.Buyer },
            { name: getTrans(SELLER_ORDER_LIST_TITLE_TEXT), type: USER_TYPE_INDEX.Seller },
          ],
          /** @ts-ignore */
          loadMoreOrder: () => {},
          showNavbar: false,
          fileUploadSlot: ProbingFormUpload as any,
          showFormItem: {
            // 只有 flow 2 会显示 user type, enquiry type, order
            showUserAndEnquiryType: probingFormExperiment === PROBING_FORM_EXP.Flow2,
            showOrderSelector: probingFormExperiment === PROBING_FORM_EXP.Flow2,
          },
          formItemTitle: {
            userType: getTrans('enquiry_details_modal_category_title'),
            enquiryType: getTrans('enquiry_details_modal_option_title'),
            problemType: getTrans(PROBING_FORM_TRANSIFY.PROBLEM_TYPE),
            order: getTrans(PROBING_FORM_TRANSIFY.ORDER),
            description: getTrans(PROBING_FORM_TRANSIFY.DESCRIPTION),
            files: getTrans(PROBING_FORM_TRANSIFY.SELECT_FILES),
          },
          stillNotFoundValueText: getTrans(PROBING_FORM_TRANSIFY.CANNOT_FIND_ORDER),
          getInitialValues,
        }}
      />
    </div>
  );
}
